import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSalesSummaryReport, createSalesSummaryReportDownload } from '../../../../APICalling/APICalling';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import Loader from '../../../../Components/Loader/Loader';
import { toast } from 'react-toastify';
import ReportHeader from '../ReportHeader/ReportHeader';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import styles from './SalesSummaryReport.module.css';
import { calculateReportDateRange, getPeriodText, isReportRangeWithinMonths } from '../../../../Utils/ReportDateUtils';
import { attachSharedUserChannelListener } from '../../../../Utils/clearSharedUserChannelEvent';
import { downloadFileWithAuth, getDownloadFilename } from '../../../../Utils/downloadFileWithAuth';
import logger from '../../../../Utils/logger';

const SalesSummaryReport = ({ filterParams }) => {
    const { selectedOutlet, userAssignedOutlets } = useGlobalContext();
    const [summaryData, setSummaryData] = useState([]);
    const [totalSummary, setTotalSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [outletName, setOutletName] = useState('');
    const [periodText, setPeriodText] = useState('');
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [pagination, setPagination] = useState(null);
    const observer = useRef();
    const fetchSummaryReportRef = useRef();

    // Get outlet name
    useEffect(() => {
        if (selectedOutlet && userAssignedOutlets?.length) {
            const outlet = userAssignedOutlets.find(o => o.id === selectedOutlet);
            setOutletName(outlet?.name || 'Unknown');
        }
    }, [selectedOutlet, userAssignedOutlets]);

    const fetchSummaryReport = useCallback(async (page = 1, isInitialLoad = false) => {
        try {
            if (isInitialLoad) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }

            if (!filterParams) {
                setSummaryData([]);
                setTotalSummary(null);
                setHasMore(false);
                return;
            }

            // Calculate dates using utility function
            const { startDate, endDate } = calculateReportDateRange(filterParams);

            const params = {
                start_date: startDate,
                end_date: endDate,
                outlet_id: filterParams.outlet || selectedOutlet,
                pagination: 1,
                page: page
            };

            if (isInitialLoad) {
                setPeriodText(getPeriodText(filterParams));
            }

            const response = await getSalesSummaryReport(params);

            const isSuccess = response?.data?.success ?? response?.success ?? false;
            if (isSuccess) {
                const dataList = Array.isArray(response?.data?.data?.list)
                    ? response.data.data.list
                    : [];
                const paginationInfo = response?.data?.data?.pagination || null;
                const totalSummaryData = response?.data?.data?.total_summary || null;

                if (isInitialLoad) {
                    setSummaryData(dataList);
                    setTotalSummary(totalSummaryData);
                } else {
                    setSummaryData(prev => [...prev, ...dataList]);
                }

                setPagination(paginationInfo);

                if (paginationInfo && paginationInfo.currentPage >= paginationInfo.lastPage) {
                    setHasMore(false);
                } else if (paginationInfo) {
                    setHasMore(true);
                } else {
                    setHasMore(false);
                }
            } else {
                if (isInitialLoad) {
                    toast.error(response?.response?.data?.message || response?.data?.message || response?.message || 'Failed to fetch sales summary report');
                    setSummaryData([]);
                    setTotalSummary(null);
                }
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error fetching sales summary report:', error);
            if (isInitialLoad) {
                toast.error('An error occurred while fetching sales summary report');
                setSummaryData([]);
                setTotalSummary(null);
            }
            setHasMore(false);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [filterParams, selectedOutlet]);

    // Store fetchSummaryReport in ref for use in observer
    useEffect(() => {
        fetchSummaryReportRef.current = fetchSummaryReport;
    }, [fetchSummaryReport]);

    // Trigger initial fetch when filterParams changes
    // Only call API if filterParams.outlet matches selectedOutlet (i.e., Generate was clicked)
    useEffect(() => {
        if (filterParams && filterParams.date && filterParams.outlet === selectedOutlet) {
            setCurrentPage(1);
            setSummaryData([]);
            setTotalSummary(null);
            setHasMore(true);
            setPagination(null);
            fetchSummaryReport(1, true);
        }
    }, [filterParams, fetchSummaryReport, selectedOutlet]);

    // Intersection Observer for infinite scroll
    const lastItemRef = useCallback(node => {
        if (loading || loadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && pagination) {
                const nextPage = currentPage + 1;
                if (nextPage <= pagination.lastPage && fetchSummaryReportRef.current) {
                    setCurrentPage(nextPage);
                    fetchSummaryReportRef.current(nextPage, false);
                }
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, loadingMore, hasMore, pagination, currentPage]);

    const formatCurrency = (value) => {
        const num = parseFloat(value || 0);
        return `$ ${num.toFixed(2)}`;
    };

    const handleDownloadCSV = async () => {
        if (!summaryData || summaryData.length === 0) {
            toast.warning('No data available to download');
            return;
        }
        if (!isReportRangeWithinMonths(filterParams, 3)) {
            toast.error('Please select a date range within 90 days to proceed.');
            return;
        }

        // Get user_id and token from localStorage
        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('token');

        if (!userId) {
            toast.error('User ID not found');
            return;
        }

        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        // Calculate dates using utility function
        const { startDate, endDate } = calculateReportDateRange(filterParams);

        // Create user-specific private channel name
        const channelName = `user.${userId}`;
        let channel;
        let toastId = null;

        setDownloadLoading(true);
        // Show persistent toast
        toastId = toast.loading("Downloading in progress...");

        try {
            // Join shared user channel — do not Echo.leave (payroll also uses user.{id}).
            const eventName = 'SalesSummaryReportReady';
            let detach = null;

            const cleanup = () => {
                try {
                    detach?.();
                } catch (e) {}
                if (toastId) {
                    toast.dismiss(toastId);
                    toastId = null;
                }
            };

            const { channel: joinedChannel, detach: detachListener } = attachSharedUserChannelListener(
                channelName,
                eventName,
                async (event) => {
                    try {
                        const downloadUrl = event?.url || event?.data?.url;

                        if (!downloadUrl) {
                            toast.error('Download URL not received. Please try again.');
                            cleanup();
                            setDownloadLoading(false);
                            return;
                        }

                        try {
                            await downloadFileWithAuth(
                                downloadUrl,
                                getDownloadFilename(event, downloadUrl, 'sales-summary.csv')
                            );
                            toast.success('Downloaded successfully');
                        } catch (downloadError) {
                            logger.error('Sales summary download failed:', downloadError);
                            toast.error('Failed to start download');
                        }

                        cleanup();
                        setDownloadLoading(false);
                    } catch (error) {
                        toast.error('Failed to process download');
                        cleanup();
                        setDownloadLoading(false);
                    }
                }
            );
            channel = joinedChannel;
            detach = detachListener;

            // Error handler
            if (typeof channel.error === 'function') {
                channel.error((error) => {
                    toast.error('Socket connection error');
                    cleanup();
                    setDownloadLoading(false);
                });
            }

            // Wait for subscription to be ready before calling API
            await new Promise((resolve) => setTimeout(resolve, 500));

            const downloadParams = {
                start_date: startDate,
                end_date: endDate,
                outlet_id: filterParams.outlet || selectedOutlet,
                pagination: 1,
                page: 1,
                user_id: userId
            };

            const createResponse = await createSalesSummaryReportDownload(downloadParams);

            const responseMessage =
                createResponse?.data?.data?.message ||
                createResponse?.data?.message ||
                createResponse?.message ||
                '';

            const isSuccess =
                createResponse?.data?.data?.success ||
                createResponse?.data?.success ||
                createResponse?.success ||
                createResponse?.data?.status === 'processing' ||
                createResponse?.data?.status === 'success';

            const isGeneratingMessage =
                typeof responseMessage === 'string' &&
                responseMessage.toLowerCase().includes('export is being generated');

            if (isSuccess || isGeneratingMessage) {
                return; // Wait for socket event
            }

            if (!isSuccess) {
                const backendError = responseMessage || 'Failed to initiate download';
                toast.error(backendError);
                cleanup();
                setDownloadLoading(false);
            }
        } catch (error) {
            console.error('Error downloading sales summary report:', error);
            toast.error(error?.message || 'An error occurred while exporting sales summary report');
            setDownloadLoading(false);
        }
    };

    return (
        <div className={styles.salesReportContainer}>
            {/* Header Section */}
            <ReportHeader
                outletName={outletName}
                periodText={periodText}
                onDownload={handleDownloadCSV}
                downloadLoading={downloadLoading}
                hasData={summaryData && summaryData.length > 0}
                downloadButtonText="Download CSV"
            />

            {/* Table Section */}
            {loading ? (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            ) : summaryData && summaryData.length > 0 ? (
                <div className={styles.tableWrapper}>
                    <table className={styles.salesTable}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr>
                                <th className={`${styles.colOrderId} ${styles.leftAlign}`}>Date</th>
                                <th className={`${styles.colQuantity} ${styles.leftAlign}`}>Services</th>
                                <th className={`${styles.colTotal} ${styles.leftAlign}`}>Service Sales</th>
                                <th className={`${styles.colQuantity} ${styles.leftAlign}`}>Products</th>
                                <th className={`${styles.colTotal} ${styles.leftAlign}`}>Product Sales</th>
                                <th className={`${styles.colMembership} ${styles.colQuantity} ${styles.leftAlign}`}>Membership</th>
                                <th className={`${styles.colMembershipSale} ${styles.colTotal} ${styles.leftAlign}`}>Membership Sale</th>
                                <th className={`${styles.colTotal} ${styles.leftAlign}`}>Subtotal</th>
                                <th className={`${styles.colTotal} ${styles.leftAlign}`}>Taxes</th>
                                <th className={`${styles.colTotal} ${styles.leftAlign}`}>Gross Total</th>
                                {/* <th className={`${styles.colTotal} ${styles.leftAlign}`}>Adjustment Total</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {summaryData.map((item, index) => {
                                const isLastItem = index === summaryData.length - 1;
                                return (
                                    <tr key={`${item?.date || 'summary'}-${index}`} ref={isLastItem ? lastItemRef : null}>
                                        <td className={styles.colOrderId}>{item?.date || 'N/A'}</td>
                                        <td className={`${styles.colQuantity} ${styles.leftAlign}`}>{item?.services ?? 0}</td>
                                        <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(item?.service_sales)}</td>
                                        <td className={`${styles.colQuantity} ${styles.leftAlign}`}>{item?.products ?? 0}</td>
                                        <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(item?.product_sales)}</td>
                                        <td className={`${styles.colMembership} ${styles.colQuantity} ${styles.leftAlign}`}>{item?.membership ?? 0}</td>
                                        <td className={`${styles.colMembershipSale} ${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(item?.membership_sale)}</td>
                                        <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(item?.subtotal)}</td>
                                        <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(item?.taxes)}</td>
                                        <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(item?.gross_total)}</td>
                                        {/* <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(item?.adjustment_total)}</td> */}
                                    </tr>
                                );
                            })}

                            {loadingMore && (
                                <tr>
                                    <td colSpan={10} className={styles.loadingMoreText}>
                                        Loading...
                                    </td>
                                </tr>
                            )}

                            {totalSummary && !hasMore && (
                                <tr className={styles.totalRow}>
                                    <td className={styles.colOrderId}>{totalSummary?.date || 'Total'}</td>
                                    <td className={`${styles.colQuantity} ${styles.leftAlign}`}>{totalSummary?.services ?? 0}</td>
                                    <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(totalSummary?.service_sales)}</td>
                                    <td className={`${styles.colQuantity} ${styles.leftAlign}`}>{totalSummary?.products ?? 0}</td>
                                    <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(totalSummary?.product_sales)}</td>
                                    <td className={`${styles.colMembership} ${styles.colQuantity} ${styles.leftAlign}`}>{totalSummary?.membership ?? 0}</td>
                                    <td className={`${styles.colMembershipSale} ${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(totalSummary?.membership_sale)}</td>
                                    <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(totalSummary?.subtotal)}</td>
                                    <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(totalSummary?.taxes)}</td>
                                    <td className={`${styles.colTotal} ${styles.leftAlign}`}>{formatCurrency(totalSummary?.gross_total)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className={styles.noData}>
                    <Placeholder
                        icon={<SearchNotFoundIcon width="24" height="24" color="white" />}
                        heading="No sales summary data found"
                        para="Please try changing the filters"
                    />
                </div>
            )}
        </div>
    );
};

export default SalesSummaryReport;
